/**
 * Announcement Bar Custom Element
 * Handles automatic rotation and manual navigation of announcements
 * Following Shopify Web Components pattern
 */

if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();

      // Core elements
      this.slider = this.querySelector('[data-announcement-slider]');
      this.slides = Array.from(this.querySelectorAll('[data-announcement-slide]'));
      this.indicators = Array.from(this.querySelectorAll('[data-announcement-indicator]'));
      this.prevButton = this.querySelector('[data-announcement-prev]');
      this.nextButton = this.querySelector('[data-announcement-next]');

      // Configuration
      this.rotationEnabled = this.dataset.rotationEnabled === 'true';
      this.rotationInterval = parseInt(this.dataset.rotationInterval, 10) || 5000;

      // State
      this.currentIndex = 0;
      this.rotationTimer = null;
      this.isPaused = false;

      // Initialize
      this.init();
    }

  /**
   * Initialize the announcement bar
   */
  init() {
    if (this.slides.length <= 1) {
      return;
    }

    this.attachEventListeners();

    if (this.rotationEnabled) {
      this.startRotation();
    }
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    // Previous button
    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.navigateToPrevious.bind(this));
    }

    // Next button
    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.navigateToNext.bind(this));
    }

    // Indicators
    this.indicators.forEach((indicator, index) => {
      indicator.addEventListener('click', () => this.navigateToIndex(index));
    });

    // Pause on hover
    this.addEventListener('mouseenter', this.pauseRotation.bind(this));
    this.addEventListener('mouseleave', this.resumeRotation.bind(this));

    // Pause on focus
    this.addEventListener('focusin', this.pauseRotation.bind(this));
    this.addEventListener('focusout', this.resumeRotation.bind(this));

    // Handle visibility change (pause when tab is hidden)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseRotation();
      } else {
        this.resumeRotation();
      }
    });
  }

  /**
   * Navigate to the previous slide
   */
  navigateToPrevious() {
    const newIndex = this.currentIndex === 0
      ? this.slides.length - 1
      : this.currentIndex - 1;

    this.navigateToIndex(newIndex);
  }

  /**
   * Navigate to the next slide
   */
  navigateToNext() {
    const newIndex = this.currentIndex === this.slides.length - 1
      ? 0
      : this.currentIndex + 1;

    this.navigateToIndex(newIndex);
  }

  /**
   * Navigate to a specific slide by index
   * @param {number} index - The slide index to navigate to
   */
  navigateToIndex(index) {
    if (index === this.currentIndex || index < 0 || index >= this.slides.length) {
      return;
    }

    // Update slides
    this.slides[this.currentIndex].removeAttribute('data-active');
    this.slides[index].setAttribute('data-active', 'true');

    // Update indicators
    if (this.indicators.length > 0) {
      this.indicators[this.currentIndex].removeAttribute('data-active');
      this.indicators[index].setAttribute('data-active', 'true');
    }

    // Update current index
    this.currentIndex = index;

    // Restart rotation timer if active
    if (this.rotationEnabled && !this.isPaused) {
      this.resetRotation();
    }
  }

  /**
   * Start automatic rotation
   */
  startRotation() {
    if (!this.rotationEnabled || this.slides.length <= 1) {
      return;
    }

    this.rotationTimer = setInterval(() => {
      this.navigateToNext();
    }, this.rotationInterval);
  }

  /**
   * Stop automatic rotation
   */
  stopRotation() {
    if (this.rotationTimer) {
      clearInterval(this.rotationTimer);
      this.rotationTimer = null;
    }
  }

  /**
   * Reset rotation timer
   */
  resetRotation() {
    this.stopRotation();
    this.startRotation();
  }

  /**
   * Pause rotation (temporarily)
   */
  pauseRotation() {
    this.isPaused = true;
    this.stopRotation();
  }

  /**
   * Resume rotation
   */
  resumeRotation() {
    this.isPaused = false;

    if (this.rotationEnabled) {
      this.startRotation();
    }
  }

  /**
   * Disconnect callback - cleanup when element is removed
   */
  disconnectedCallback() {
    this.stopRotation();
  }
}

customElements.define('announcement-bar', AnnouncementBar);
}
